
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { 
  FileText, Send, CheckCircle, Clock, Plus, Sparkles, 
  Loader2, X, ShieldCheck, Mail, MapPin, User,
  ChevronRight, ArrowRight, Download, Eye, ExternalLink,
  Search, AlertCircle, RefreshCw, FileCheck, Trash2,
  ChevronLeft, Info, Library, Save, Layout, Edit2,
  Tag, Code, Building, Receipt, Calendar, ArrowLeft, Printer,
  FileDown,
  Type,
  List,
  Bold,
  Heading2
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { Agreement, AgreementTemplate, Inspection, Location, Contact, Service, Invoice } from '../types';
import { supabase, toSnakeCase, toCamelCase, getErrorMessage } from '../lib/supabase';

interface AgreementsProps {
  agreements: Agreement[];
  setAgreements: React.Dispatch<React.SetStateAction<Agreement[]>>;
  inspections: Inspection[];
  properties: Location[];
  contacts: Contact[];
  services: Service[];
  invoices: Invoice[];
}

const Agreements: React.FC<AgreementsProps> = ({ 
  agreements, setAgreements, inspections, properties, contacts, services, invoices 
}) => {
  const [activeView, setActiveView] = useState<'registry' | 'templates'>('registry');
  const [selectedAgreementId, setSelectedAgreementId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  const [templates, setTemplates] = useState<AgreementTemplate[]>([]);
  const [isTemplateSidebarOpen, setIsTemplateSidebarOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [templateFormData, setTemplateFormData] = useState<Partial<AgreementTemplate>>({ title: '', content: '', serviceId: '' });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isSending, setIsSending] = useState<string | null>(null);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  const agreementPdfRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    const { data, error } = await supabase.from('agreement_templates').select('*');
    if (!error && data) setTemplates(data.map(toCamelCase));
    setLoading(false);
  };

  const getContact = (id: string) => contacts.find(c => c.id === id);
  const getProperty = (id: string) => properties.find(p => p.id === id);
  const getInspection = (id: string) => inspections.find(i => i.id === id);

  const selectedAgreement = useMemo(() => 
    agreements.find(a => a.id === selectedAgreementId),
    [agreements, selectedAgreementId]
  );

  const filteredAgreements = useMemo(() => {
    return agreements.filter(a => {
      const contact = getContact(inspections.find(i => i.id === a.inspectionId)?.contactId || '');
      const search = searchTerm.toLowerCase();
      return (
        (a.title || '').toLowerCase().includes(search) ||
        (contact?.name || '').toLowerCase().includes(search)
      );
    }).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [agreements, searchTerm, inspections, contacts]);

  const handleSaveTemplate = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = toSnakeCase(templateFormData);
      if (editingId) {
        const { error } = await supabase.from('agreement_templates').update(payload).eq('id', editingId);
        if (error) throw error;
        setTemplates(prev => prev.map(t => t.id === editingId ? { ...t, ...templateFormData } as AgreementTemplate : t));
      } else {
        const { data, error } = await supabase.from('agreement_templates').insert([payload]).select().single();
        if (error) throw error;
        setTemplates(prev => [toCamelCase(data), ...prev]);
      }
      setIsTemplateSidebarOpen(false);
    } catch (err) { alert(getErrorMessage(err)); }
    finally { setSaving(false); }
  };

  const handleDownloadPDF = async () => {
    if (!selectedAgreement || !agreementPdfRef.current) return;
    setIsGeneratingPDF(true);
    try {
      const element = agreementPdfRef.current;
      
      const canvas = await html2canvas(element, { 
        scale: 1.5, 
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false,
        windowWidth: 800,
        ignoreElements: (el) => el.classList.contains('no-print')
      });
      
      const pdf = new jsPDF({
        orientation: 'p',
        unit: 'mm',
        format: 'a4',
        compress: true
      });
      
      const imgData = canvas.toDataURL('image/jpeg', 0.70);
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      const margin = 20; 
      const innerWidth = pdfWidth - (margin * 2);
      const innerHeight = pdfHeight - (margin * 2);
      
      const pxPageHeight = (innerHeight * canvas.width) / innerWidth;
      let leftHeight = canvas.height;
      let positionY = 0;
      let page = 1;

      while (leftHeight > 0) {
        if (page > 1) pdf.addPage();
        
        pdf.addImage(
          imgData, 
          'JPEG', 
          margin, 
          margin - (positionY * innerWidth / canvas.width), 
          innerWidth, 
          (canvas.height * innerWidth / canvas.width),
          undefined,
          'FAST'
        );

        pdf.setFillColor(255, 255, 255);
        pdf.rect(0, pdfHeight - margin, pdfWidth, margin, 'F');
        pdf.rect(0, 0, pdfWidth, margin, 'F');

        positionY += pxPageHeight;
        leftHeight -= pxPageHeight;
        page++;
        
        if (leftHeight < 15) break;
      }

      const associatedInvoice = invoices.find(inv => inv.inspectionId === selectedAgreement.inspectionId);
      const referenceId = associatedInvoice?.invoiceNumber || selectedAgreement.id.substring(0, 8);
      pdf.save(`Verified_Agreement_${referenceId}.pdf`);
    } catch (err) {
      alert(`PDF Generation Failure: ${getErrorMessage(err)}`);
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleDispatchAgreement = async (agreement: Agreement) => {
    const inspection = inspections.find(i => i.id === agreement.inspectionId);
    const contact = getContact(inspection?.contactId || '');
    if (!contact?.email) return alert("Stakeholder email required.");

    setIsSending(agreement.id);
    try {
      const signUrl = `${window.location.origin}/sign/${agreement.id}`;
      const payload = {
        to: contact.email,
        customerName: contact.name,
        invoiceNumber: "Sign Link",
        amount: "0.00", 
        propertyAddress: properties.find(p => p.id === inspection?.propertyId)?.address || "Asset",
        businessName: "Registry Hub",
        paymentUrl: signUrl, 
        emailType: 'signing_request',
        agreementTitle: agreement.title
      };
      const { error } = await supabase.functions.invoke('smtp2go', { body: payload });
      if (error) throw error;
      await supabase.from('agreements').update({ status: 'Sent' }).eq('id', agreement.id);
      setAgreements(prev => prev.map(a => a.id === agreement.id ? { ...a, status: 'Sent' } : a));
    } catch (err) { alert(getErrorMessage(err)); }
    finally { setIsSending(null); }
  };

  const handleDeleteAgreement = async (id: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (!confirm('Purge this record?')) return;
    try {
      await supabase.from('agreements').delete().eq('id', id);
      setAgreements(prev => prev.filter(a => a.id !== id));
      if (selectedAgreementId === id) setSelectedAgreementId(null);
    } catch (err) { alert(getErrorMessage(err)); }
  };

  if (selectedAgreementId && selectedAgreement) {
    const inspection = getInspection(selectedAgreement.inspectionId);
    const contact = getContact(inspection?.contactId || '');
    const property = getProperty(inspection?.propertyId || '');

    return (
      <div className="animate-in slide-in-from-right-2 duration-300 space-y-4 pb-20">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center bg-white p-4 rounded-xl border border-slate-200 shadow-sm gap-4">
          <div className="flex items-center gap-3">
            <button onClick={() => setSelectedAgreementId(null)} className="p-2 hover:bg-slate-100 rounded-lg text-slate-400 transition-all"><ChevronLeft size={20} /></button>
            <h3 className="text-sm font-bold text-slate-800">Compliance Preview</h3>
          </div>
          <div className="flex gap-2">
            <button disabled={isGeneratingPDF} onClick={handleDownloadPDF} className="px-5 py-2 bg-white border border-slate-200 text-slate-700 rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-slate-50 transition-all flex items-center gap-2">
              {isGeneratingPDF ? <Loader2 size={12} className="animate-spin" /> : <Printer size={12} />} Save PDF
            </button>
            {selectedAgreement.status !== 'Signed' && (
              <button disabled={isSending === selectedAgreement.id} onClick={() => handleDispatchAgreement(selectedAgreement)} className="px-5 py-2 bg-slate-900 text-white rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-slate-800 transition-all flex items-center gap-2">
                {isSending === selectedAgreement.id ? <Loader2 size={12} className="animate-spin" /> : <Send size={10} />} Dispatch
              </button>
            )}
            <button onClick={(e) => handleDeleteAgreement(selectedAgreement.id, e)} className="p-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100"><Trash2 size={16} /></button>
          </div>
        </div>

        <div className="max-w-[800px] mx-auto">
          <div ref={agreementPdfRef} className="bg-white border border-slate-200 shadow-xl overflow-hidden p-12 md:p-16 lg:p-24">
             <div className="flex justify-between items-start mb-12 border-b-2 border-slate-900 pb-8">
                <div>
                   <h1 className="text-3xl font-black text-slate-900 mb-1 leading-tight uppercase tracking-tight">{selectedAgreement.title}</h1>
                   <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Digital Registry ID: REG-{selectedAgreement.id.substring(0, 10).toUpperCase()}</p>
                </div>
                <div className="text-right">
                   <p className="text-[10px] font-black text-slate-900 uppercase tracking-widest">Protocol Date</p>
                   <p className="text-sm font-bold text-slate-500">{new Date(selectedAgreement.createdAt).toLocaleDateString()}</p>
                </div>
             </div>

             <div className="prose prose-slate max-w-none mb-16">
                <div className="text-slate-800 text-[13pt] leading-[1.6] whitespace-pre-wrap font-medium">
                   <ReactMarkdown remarkPlugins={[remarkGfm]}>{selectedAgreement.content}</ReactMarkdown>
                </div>
             </div>

             {selectedAgreement.status === 'Signed' && selectedAgreement.signatureData && (
                <div className="mt-12 pt-10 border-t-2 border-slate-900 w-fit min-w-[300px]">
                   <div className="mb-4">
                      <img src={selectedAgreement.signatureData} className="h-16 object-contain" alt="Signature" />
                   </div>
                   <div className="space-y-0.5">
                      <p className="text-[11px] font-black uppercase text-slate-900 tracking-widest">Verified Affirmation</p>
                      <p className="text-[9px] font-bold text-slate-400 uppercase">Registry Authenticated: {new Date(selectedAgreement.signedAt!).toLocaleString()}</p>
                   </div>
                </div>
             )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-in fade-in duration-300 pb-20">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div>
          <h3 className="text-lg font-black text-slate-800 uppercase tracking-tight">Compliance Center</h3>
        </div>
        <div className="flex bg-slate-100 p-1 rounded-lg border border-slate-200">
           <button onClick={() => setActiveView('registry')} className={`px-4 py-1.5 rounded-md text-[9px] font-black uppercase tracking-widest transition-all ${activeView === 'registry' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}>Registry</button>
           <button onClick={() => setActiveView('templates')} className={`px-4 py-1.5 rounded-md text-[9px] font-black uppercase tracking-widest transition-all ${activeView === 'templates' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}>Templates</button>
        </div>
      </div>

      {activeView === 'templates' ? (
        <div className="space-y-4">
           <div className="flex justify-between items-center">
              <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Master Library</h4>
              <button onClick={() => { setEditingId(null); setTemplateFormData({title: '', content: '', serviceId: ''}); setIsTemplateSidebarOpen(true); }} className="bg-slate-900 text-white px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest shadow-md flex items-center gap-2"><Plus size={14} /> New Template</button>
           </div>
           <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {templates.map(t => (
                <div key={t.id} className="bg-white p-6 rounded-xl border border-slate-200 hover:border-slate-300 transition-all flex flex-col justify-between">
                   <div>
                      <div className="flex justify-between items-start mb-4">
                        <div className="p-2 bg-slate-50 text-slate-400 rounded-lg"><Library size={18} /></div>
                        <div className="flex gap-1">
                           <button onClick={() => { setEditingId(t.id); setTemplateFormData(t); setIsTemplateSidebarOpen(true); }} className="p-1.5 text-slate-300 hover:text-blue-600"><Edit2 size={14} /></button>
                           <button onClick={async () => { if(confirm('Purge template?')) { await supabase.from('agreement_templates').delete().eq('id', t.id); fetchTemplates(); }}} className="p-1.5 text-slate-300 hover:text-red-500"><Trash2 size={14} /></button>
                        </div>
                      </div>
                      <h5 className="font-bold text-slate-800 text-sm mb-1">{t.title}</h5>
                      <p className="text-[10px] text-slate-400 font-bold uppercase mb-4">{services.find(s => s.id === t.serviceId)?.name || 'Manual'}</p>
                   </div>
                </div>
              ))}
           </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden overflow-x-auto no-scrollbar">
            <table className="w-full text-left min-w-max">
              <thead><tr className="bg-slate-50/50 border-b border-slate-200"><th className="px-6 py-4 text-[9px] font-black text-slate-500 uppercase tracking-widest">Protocol</th><th className="px-6 py-4 text-[9px] font-black text-slate-500 uppercase tracking-widest">Stakeholder</th><th className="px-6 py-4 text-[9px] font-black text-slate-500 uppercase tracking-widest text-center">Status</th><th className="px-6 py-4 text-[9px] font-black text-slate-500 uppercase tracking-widest text-right">Actions</th></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {filteredAgreements.map(a => {
                  const ins = inspections.find(i => i.id === a.inspectionId);
                  const contact = getContact(ins?.contactId || '');
                  return (
                    <tr key={a.id} onClick={() => setSelectedAgreementId(a.id)} className="hover:bg-slate-50/80 transition-colors cursor-pointer group">
                      <td className="px-6 py-4"><div className="flex items-center gap-3"><div className={`w-8 h-8 rounded-lg flex items-center justify-center ${a.status === 'Signed' ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-400'}`}><ShieldCheck size={16} /></div><p className="text-sm font-bold text-slate-800 truncate max-w-[250px]">{a.title}</p></div></td>
                      <td className="px-6 py-4"><p className="text-xs font-bold text-slate-600">{contact?.name || '--'}</p></td>
                      <td className="px-6 py-4 text-center"><span className={`text-[8px] font-black px-2 py-0.5 rounded-md uppercase tracking-wider border ${a.status === 'Signed' ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : 'bg-blue-50 text-blue-600 border-blue-100'}`}>{a.status}</span></td>
                      <td className="px-6 py-4 text-right"><ChevronRight size={16} className="text-slate-200 inline-block" /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Template Editor Sidebar with Markdown & Tag Guides */}
      <div className={`fixed inset-0 z-[300] ${isTemplateSidebarOpen ? 'visible' : 'invisible'}`}>
        <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={() => setIsTemplateSidebarOpen(false)} />
        <div className={`absolute inset-y-0 right-0 w-full md:max-w-6xl bg-white shadow-2xl transition-transform duration-500 ${isTemplateSidebarOpen ? 'translate-x-0' : 'translate-x-full'} flex flex-col`}>
          <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
            <h3 className="font-bold text-slate-800 text-xs uppercase tracking-widest">{editingId ? 'Modify Template' : 'New Template'}</h3>
            <button onClick={() => setIsTemplateSidebarOpen(false)} className="p-2 hover:bg-white rounded-lg text-slate-400"><X size={24} /></button>
          </div>
          
          <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
            {/* Main Form Area */}
            <form onSubmit={handleSaveTemplate} className="flex-1 overflow-y-auto p-6 md:p-8 space-y-6 no-scrollbar order-1">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1"><label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Title</label><input required className="w-full p-3 bg-slate-50 border border-slate-200 rounded-lg font-bold text-sm" value={templateFormData.title} onChange={(e)=>setTemplateFormData({...templateFormData, title: e.target.value})} /></div>
                <div className="space-y-1"><label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Association</label><select className="w-full p-3 bg-slate-50 border border-slate-200 rounded-lg font-bold text-sm" value={templateFormData.serviceId} onChange={(e)=>setTemplateFormData({...templateFormData, serviceId: e.target.value})}><option value="">None</option>{services.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}</select></div>
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest flex items-center gap-2">
                  <FileText size={12} /> Template Content
                </label>
                <textarea required rows={22} className="w-full p-6 bg-slate-50 border border-slate-200 rounded-xl font-mono text-[13px] leading-[1.6] text-slate-800 resize-none outline-none focus:ring-1 focus:ring-[#606c38] focus:bg-white transition-all" value={templateFormData.content} onChange={(e)=>setTemplateFormData({...templateFormData, content: e.target.value})} placeholder="Define your protocol using Markdown..." />
              </div>
            </form>

            {/* Guides Panel (Sticky Right on Desktop) */}
            <aside className="w-full lg:w-[320px] bg-slate-50 border-l border-slate-100 overflow-y-auto p-8 space-y-10 order-2">
              <section>
                <h4 className="text-[10px] font-black text-slate-900 uppercase tracking-[0.2em] mb-6 flex items-center gap-2">
                  <Tag size={14} className="text-[#606c38]" /> Dynamic Registry Tags
                </h4>
                <p className="text-[11px] text-slate-500 leading-relaxed mb-6">
                  Use these case-sensitive brackets. Our synchronization engine will automatically replace them with real session data.
                </p>
                <div className="space-y-3">
                  {[
                    { tag: '[CLIENT_NAME]', desc: 'Full legal name of stakeholder' },
                    { tag: '[ADDRESS]', desc: 'Complete property address' },
                    { tag: '[FEE]', desc: 'Total calculated session fee' },
                    { tag: '[DATE]', desc: 'The scheduled day of session' }
                  ].map(t => (
                    <div key={t.tag} className="p-3 bg-white border border-slate-200 rounded-xl shadow-sm group hover:border-[#606c38] transition-colors cursor-default">
                      <code className="text-[11px] font-black text-[#606c38] bg-[#606c38]/5 px-1.5 py-0.5 rounded">{t.tag}</code>
                      <p className="text-[10px] text-slate-400 mt-1.5 font-bold uppercase tracking-tight">{t.desc}</p>
                    </div>
                  ))}
                </div>
              </section>

              <section>
                <h4 className="text-[10px] font-black text-slate-900 uppercase tracking-[0.2em] mb-6 flex items-center gap-2">
                  <Code size={14} className="text-blue-500" /> Markdown Format Guide
                </h4>
                <div className="space-y-4">
                   <div className="flex items-start gap-4">
                      <div className="p-2 bg-blue-50 text-blue-500 rounded-lg"><Heading2 size={14} /></div>
                      <div><code className="text-[11px] font-bold text-slate-800"># Heading 1</code><p className="text-[9px] text-slate-400 uppercase font-bold mt-1">Registry titles</p></div>
                   </div>
                   <div className="flex items-start gap-4">
                      <div className="p-2 bg-blue-50 text-blue-500 rounded-lg"><Bold size={14} /></div>
                      <div><code className="text-[11px] font-bold text-slate-800">**Bold Text**</code><p className="text-[9px] text-slate-400 uppercase font-bold mt-1">Crucial terminology</p></div>
                   </div>
                   <div className="flex items-start gap-4">
                      <div className="p-2 bg-blue-50 text-blue-500 rounded-lg"><List size={14} /></div>
                      <div><code className="text-[11px] font-bold text-slate-800">* Bullet Point</code><p className="text-[9px] text-slate-400 uppercase font-bold mt-1">Exclusion lists</p></div>
                   </div>
                </div>
              </section>

              <div className="p-5 bg-white border border-slate-200 rounded-2xl">
                 <div className="flex items-center gap-2 text-blue-600 mb-2">
                    <Sparkles size={14} />
                    <span className="text-[9px] font-black uppercase tracking-widest">Inspector Tip</span>
                 </div>
                 <p className="text-[10px] text-slate-500 leading-relaxed font-medium">
                   Markdown is industrial-standard for technical documentation. It ensures your PDFs look clean across all devices.
                 </p>
              </div>
            </aside>
          </div>

          <div className="p-6 border-t border-slate-100 flex gap-3 bg-white shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.02)]">
            <button type="button" onClick={() => setIsTemplateSidebarOpen(false)} className="flex-1 py-3 text-[10px] font-black uppercase tracking-widest text-slate-500 hover:bg-slate-50 rounded-lg border border-slate-200 transition-all">Cancel</button>
            <button disabled={saving} onClick={handleSaveTemplate} className="flex-[2] py-3 bg-slate-900 text-white font-black text-[10px] uppercase tracking-widest rounded-lg hover:bg-slate-800 shadow-lg transition-all active:scale-[0.99] flex items-center justify-center gap-2">
              {saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />} Save Protocol Template
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Agreements;
